// src/encounter/note-sign-bar.tsx
//
// List A9 (`visitNoteV2`; Antonin's catalog `#/workspace`, SignActions `SignBar` + BlockerChip `plain`, scrips-signal-ds @62cb3650):
// the encounter's floating sign bar, sticky at the bottom of the note's own scroller. It says what is still needed before signing
// ("N things to fix before you can sign", a plain text button that opens the list above it; each item goes to its blank), or, with
// nothing left, "Ready to sign" and a "Go to Sign" jump.
//
// It has NO Sign of its own (ruling 3 Nov, with PROD-3419: the note has one Sign, at the bottom of the Wrap-up card, #897): the Sign,
// its gates and its refused reason stay in the note's footer; "Go to Sign" only moves there. The items are the note's required blanks
// (`signBlanks`, the same list as "Before you sign").
import { useEffect, useRef, useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Check } from 'lucide-react';

export interface NoteSignBarItem {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface NoteSignBarProps {
  items: readonly NoteSignBarItem[];
  /** Moves to the footer's Sign (scroll and focus). */
  onGoToSign: () => void;
  /** Why the Sign is refused or blocked right now (the page's refused reason, else its gate's). With no blank left, the bar says this
   *  instead of "Ready to sign": the bar never calls a note ready that the Sign will refuse. */
  blockedReason?: string;
}

export function NoteSignBar({ items, onGoToSign, blockedReason }: NoteSignBarProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const show = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    // One pending close at a time: a second leave replaces the first instead of leaving it to fire.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 200);
  };
  // A pending close never runs after the bar has gone.
  useEffect(() => () => clearTimeout(timer.current), []);
  const n = items.length;
  const label = `${n} ${n === 1 ? 'thing' : 'things'} to fix before you can sign`;
  return (
    <div data-slot="note-sign-bar-host">
      <div role="region" aria-label="Before you sign" data-slot="note-sign-bar">
        {n > 0 ? (
          <div
            data-slot="note-sign-bar-blockers"
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hide();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
          >
            <button type="button" data-slot="note-sign-bar-count" aria-expanded={open} onClick={show}>
              {label}
            </button>
            {open ? (
              <ul data-slot="note-sign-bar-list" aria-label="Still needed">
                {items.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => {
                        it.onSelect();
                        setOpen(false);
                      }}
                    >
                      {it.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <>
            {blockedReason ? (
              <span data-slot="note-sign-bar-blocked">Not signed yet: {blockedReason}</span>
            ) : (
              <span data-slot="note-sign-bar-ready">
                <Check className="size-4" aria-hidden />
                Ready to sign
              </span>
            )}
            <Button variant="secondary" size="sm" onClick={onGoToSign}>
              Go to Sign
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
